import { FileBulkActionRequest } from './../models/file-bulk-action-request';
import { LoadFilesRequest } from './../models/load-files-request';
import { FolderActionRequest } from './../models/folder-action-request';
import { FileAssignComponentsRequest } from './../models/file-assign-components-request';
import { ApiHelperService, ApiService } from '../../../../shared/services/api';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { BlobWithFilename } from 'app/shared/models';
import { FileActionRequest, FileProjectRequest } from '../models';
import { catchError, map } from 'rxjs/operators';
import { AnalyticsObjectType, AnalyticsService, AnalyticsUiLocation } from 'app/shared/analytics';

@Injectable()
export class FileService {

  endpointUrl: string;

  constructor(private api: ApiService,
    private analyticsService: AnalyticsService,
    { fileServiceUrl }: ApiHelperService) {
    this.endpointUrl = `${fileServiceUrl}/api/file`;
  }

  public getFilesByProperty(request: LoadFilesRequest) {
    let uri = `${this.endpointUrl}/${request.propertyId}?search=${request.searchFilter || ''}`;
    if (request.folderId != null) {
      uri = uri + `&folderId=${request.folderId}`;
    }

    return this.api.get(uri).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  public getFoldersByProperty(propertyId: string) {
    const uri = `${this.endpointUrl}/${propertyId}/folders`;
    return this.api.get(uri).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  public createPropertyFolder(request: FolderActionRequest) {
    const uri = `${this.endpointUrl}/${request.propertyId}/folder`;
    return this.api.post(uri, request).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  public renamePropertyFolder(request: FolderActionRequest) {
    const uri = `${this.endpointUrl}/${request.propertyId}/folder/${request.folderId}/rename`;
    return this.api.put(uri, request).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  public movePropertyFolder(request: FolderActionRequest) {
    const uri = `${this.endpointUrl}/${request.propertyId}/folder/${request.folderId}/move`;
    return this.api.put(uri, request).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  public deletePropertyFolder(request: FolderActionRequest) {
    const uri = `${this.endpointUrl}/${request.propertyId}/folder/${request.folderId}`;
    return this.api.delete(uri).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  public renamePropertyFile(request: FileActionRequest) {
    const uri = `${this.endpointUrl}/${request.propertyId}/file/${request.propertyFileId}/rename`;
    return this.api.put(uri, request).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  public movePropertyFile(request: FileActionRequest) {
    const uri = `${this.endpointUrl}/${request.propertyId}/file/${request.propertyFileId}/move`;
    return this.api.put(uri, request).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  public deletePropertyFile(request: FileActionRequest) {
    this.analyticsService.fileDeletedEvent(AnalyticsObjectType.Property, AnalyticsUiLocation.Files);
    const uri = `${this.endpointUrl}/${request.propertyId}/file/${request.propertyFileId}`;
    return this.api.delete(uri).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  public assignComponents(request: FileAssignComponentsRequest) {
    const uri = `${this.endpointUrl}/${request.propertyId}/bulk/assign`;
    return this.api.put(uri, request).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  public downloadPropertyFile(request: FileActionRequest): Observable<BlobWithFilename> {
    this.analyticsService.fileDownloadedEvent();

    return this.api
      .getBlobWithFilename(`${this.endpointUrl}/${request.propertyId}/file/${request.propertyFileId}/download`, 'application/octet-stream').pipe(
        catchError((error) => this.handleError(error)));
  }

  public bulkMovePropertyFiles(request: FileBulkActionRequest) {

    return this.api.post(`${this.endpointUrl}/${request.propertyId}/bulk/move`, request).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  public bulkDeletePropertyFiles(request: FileBulkActionRequest) {
    this.analyticsService.fileDeletedEvent(AnalyticsObjectType.Property, AnalyticsUiLocation.FilesBulkEdit, request.fileIds.length);

    return this.api.post(`${this.endpointUrl}/${request.propertyId}/bulk/delete`, request).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  public bulkDownloadPropertyFiles(request: FileBulkActionRequest): Observable<BlobWithFilename> {
    this.analyticsService.fileDownloadedEvent(AnalyticsUiLocation.FilesBulkEdit, request.fileIds.length);

    return this.api
      .postBlobWithFilename(`${this.endpointUrl}/${request.propertyId}/bulk/download`, request, 'application/octet-stream').pipe(
        catchError((error) => this.handleError(error)));
  }

  public removeFromProject(propertyId: string, propertyFileId: string, projectId: string) {
    const uri = `${this.endpointUrl}/${propertyId}/file/${propertyFileId}/project/${projectId}`;
    return this.api.delete(uri).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  public updateFileProject(request: FileProjectRequest) {
    const uri = `${this.endpointUrl}/${request.propertyId}/file/${request.fileId}/project/${request.projectId}`;
    return this.api.post(uri, request).pipe(
      map(response => response.data),
      catchError((error) => this.handleError(error)));
  }

  private handleError(error: any) {
    // TODO: handle error
    // eslint-disable-next-line no-console
    console.error(error);
    return throwError(() => error);
  }
}
